import { CircularProgress } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import React, { useRef, useContext, useEffect, useReducer, useState } from "react";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";

import DataContext from "../../../../../context/DataContext";
import { useQueryParams } from "../../../../../hooks/useQueryParams";
import PermissionWrapper from "../../../../global/PermissionWrapper";
import { useDrawerFilters } from "../../../../modules/drawer-filters";
import PaginationDefault from "../../../../ui library/components/paginations/pagination-default";

import { providerManagerFilterConfig } from "./config/filterConfig";
import { PMDispatchContext } from "./context/PMDispatchContext";
import { PMStateContext } from "./context/PMStateContext";
import ProviderFilters from "./ProviderFilters";
import { ProviderList } from "./ProviderList";
import { initialState, pmReducer } from "./state/pmReducer";

//TODO: Permissions via current user service

const ProviderManager = () => {
  const { providers, resultCount } = useLoaderData();
  const { filters, updateFilter } = useQueryParams();
  const { filterState, activeFilters } = useDrawerFilters(providerManagerFilterConfig);
  const { userRoles } = useContext(DataContext);
  const [state, dispatch] = useReducer(pmReducer, initialState);
  const [filteredProviders, setFilteredProviders] = useState(providers);
  const [canViewProviders, setCanViewProviders] = useState(true);
  const hasInitializedDefaultsRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  const scrollRef = useRef(null);

  const scrollToTop = () => {
    scrollRef.current.scroll({
      top: 0,
    });
  };

  useEffect(() => {
    if (hasInitializedDefaultsRef.current) {
      return;
    }

    hasInitializedDefaultsRef.current = true;
    const defaultFilters = new URLSearchParams(filters);
    let hasMissingDefaults = false;

    if (!filters.get("limit")) {
      defaultFilters.set("limit", 100);
      hasMissingDefaults = true;
    }

    if (!filters.get("ordering")) {
      defaultFilters.set("ordering", "name");
      hasMissingDefaults = true;
    }

    if (!filters.get("offset")) {
      defaultFilters.set("offset", 0);
      hasMissingDefaults = true;
    }

    if (hasMissingDefaults) {
      navigate(`${location.pathname}?${defaultFilters.toString()}`, { replace: true });
    }
  }, [filters, location.pathname, navigate]);

  useEffect(() => {
    let active = true;
    if (active) {
      setFilteredProviders(providers);
      if (scrollRef.current) {
        scrollToTop();
      }
    }
    return () => {
      active = false;
    };
  }, [providers, filters]);

  useEffect(() => {
    setCanViewProviders(userRoles.permissions.includes("provider.change_provider"));
  }, [userRoles?.permissions]);

  const handleOnDetailClick = (id) => {
    navigate(`/provider/${id}/location-info/hours-of-operation`);
  };
  return (
    <div data-testid="provider-manager" className="w-full overflow-hidden bg-white">
      <PermissionWrapper permission={canViewProviders}>
        <PMStateContext.Provider value={state}>
          <PMDispatchContext.Provider value={dispatch}>
            <CssBaseline />
            <div className="p-6" data-testid="provider-manager-main">
              <div className="my-2">
                <h1 className="text-[1.8rem] font-medium">Manage Providers</h1>
                <ProviderFilters
                  filters={filters}
                  updateFilter={updateFilter}
                  filterState={filterState}
                  activeFilters={activeFilters}
                />
              </div>
              <div className="flex flex-col gap-y-4">
                <div className="w-full">
                  {!filteredProviders ? (
                    <div className="my-24 flex justify-center">
                      <CircularProgress color="secondary" />
                    </div>
                  ) : (
                    <div data-testid="provider-list">
                      <ProviderList
                        referenceForScrolling={scrollRef}
                        data={filteredProviders}
                        handleClick={handleOnDetailClick}
                        filters={filters}
                        updateFilter={updateFilter}
                      />
                    </div>
                  )}
                </div>
                <div className="mx-auto mt-auto w-fit">
                  <PaginationDefault
                    filters={filters}
                    updateFilter={updateFilter}
                    totalCount={resultCount}
                  />
                </div>
              </div>
            </div>
          </PMDispatchContext.Provider>
        </PMStateContext.Provider>
      </PermissionWrapper>
      <PermissionWrapper permission={!canViewProviders}>
        <div data-testid="no-access-message">You do not have permission to access.</div>
      </PermissionWrapper>
    </div>
  );
};

export default ProviderManager;
